import { AnnouncementsPage } from '@backstage-community/plugin-announcements';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { AlertDisplay, OAuthRequestDialog, SignInPage, SignInProviderConfig } from '@backstage/core-components';
import { IdentityApi, microsoftAuthApiRef } from '@backstage/core-plugin-api';
import { ApiExplorerPage } from '@backstage/plugin-api-docs';
import { CatalogEntityPage, CatalogIndexPage, catalogPlugin } from '@backstage/plugin-catalog';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { CatalogUnprocessedEntitiesPage } from '@backstage/plugin-catalog-unprocessed-entities';
import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { NotificationsPage, UserNotificationSettingsCard } from '@backstage/plugin-notifications';
import { orgPlugin } from '@backstage/plugin-org';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { SearchPage } from '@backstage/plugin-search';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { TechDocsIndexPage, TechDocsReaderPage, techdocsPlugin } from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { SettingsLayout, UserSettingsPage } from '@backstage/plugin-user-settings';
import { UnifiedThemeProvider } from '@backstage/theme';
import { ReleaseNotesPage } from '@internal/plugin-release-notes';
import { DocFeedbackAddon, DocFeedbackPage, DocHelpfulAddon } from '@internal/plugin-doc-feedback';
import { SelfServicePage } from '@internal/plugin-self-service';
import Brightness2Icon from '@material-ui/icons/Brightness2';
import Brightness5Icon from '@material-ui/icons/Brightness5';
import LandscapeIcon from '@material-ui/icons/Landscape';
import NightsStayIcon from '@material-ui/icons/NightsStay';
import WbSunnyIcon from '@material-ui/icons/WbSunny';
import WebIcon from '@material-ui/icons/Web';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
import React from 'react';
import { Navigate, Route } from 'react-router-dom';
import { apis } from './apis';
import { Root } from './components/Root';
import { columns as apiDocsColumns } from './components/api-docs/CustomColumns';
import { customColumnsFunction } from './components/catalog/CustomColumns';
import { CustomFilters } from './components/catalog/CustomFilters';
import { entityPage } from './components/catalog/EntityPage';
import { HomePage } from './components/home/HomePage';
import { searchPage } from './components/search/SearchPage';
import { DocTextSize, HideDocNavTitle } from './components/techdocs';
import { caeDarkTheme, caeLightTheme } from './theme/CaeThemes';
import { devHubAuroraTheme, devHubHorizonTheme, devHubMidnightTheme } from './theme/DevHubThemes';

// Define the props specifically for your customized SignInPage
interface CustomSignInPageProps {
  onSignInSuccess: (identityApi: IdentityApi) => void;
  children?: React.ReactNode;
}

const microsoftAuthProvider: SignInProviderConfig = {
  id: 'azure-auth-provider',
  title: 'Microsoft Active Directory',
  message: 'Sign in to the IDP using your Active Directory account.',
  apiRef: microsoftAuthApiRef,
};

// Custom SignInPage accepting the specified props
const AppSignInPage: React.FC<CustomSignInPageProps> = ({ onSignInSuccess, children }) => {
  return (
    <SignInPage provider={microsoftAuthProvider} onSignInSuccess={onSignInSuccess}>
      {children}
    </SignInPage>
  );
};

export { AppSignInPage };

const app = createApp({
  apis,
  icons: {
    alert: WebIcon,
  },
  themes: [
    {
      id: 'devhub-aurora',
      title: 'Aurora',
      variant: 'light',
      icon: <WbSunnyIcon />,
      Provider: ({ children }) => <UnifiedThemeProvider theme={devHubAuroraTheme} children={children} />,
    },
    {
      id: 'devhub-midnight',
      title: 'Midnight',
      variant: 'dark',
      icon: <NightsStayIcon />,
      Provider: ({ children }) => <UnifiedThemeProvider theme={devHubMidnightTheme} children={children} />,
    },
    {
      id: 'devhub-horizon',
      title: 'Horizon',
      variant: 'light',
      icon: <LandscapeIcon />,
      Provider: ({ children }) => <UnifiedThemeProvider theme={devHubHorizonTheme} children={children} />,
    },
    {
      id: 'cae-light-theme',
      title: 'Classic Light',
      variant: 'light',
      icon: <Brightness5Icon />,
      Provider: ({ children }) => <UnifiedThemeProvider theme={caeLightTheme} children={children} />,
    },
    {
      id: 'cae-dark-theme',
      title: 'Classic Dark',
      variant: 'dark',
      icon: <Brightness2Icon />,
      Provider: ({ children }) => <UnifiedThemeProvider theme={caeDarkTheme} children={children} />,
    },
    // {
    //   id: 'cae-test',
    //   title: 'CAE Test',
    //   variant: 'dark',
    //   Provider: ({ children }) => <UnifiedThemeProvider theme={caeTestTheme} children={children} />,
    // },
  ],
  components: {
    SignInPage: AppSignInPage,
  },
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(scaffolderPlugin.externalRoutes, {
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>
    ;
    <Route path="/" element={<Navigate to="catalog" />} />
    <Route path="/catalog" element={<CatalogIndexPage filters={<CustomFilters />} columns={customColumnsFunction}/>} />
    <Route path="/catalog/:namespace/:kind/:name" element={<CatalogEntityPage />}>
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route path="/docs/:namespace/:kind/:name/*" element={<TechDocsReaderPage />}>
      <TechDocsAddons>
        <Mermaid />
        <HideDocNavTitle />
        <DocTextSize />
        <DocFeedbackAddon />
        <DocHelpfulAddon />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage columns={apiDocsColumns} />} />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />}>
      <SettingsLayout.Route path="/notifications" title="Notifications">
        <UserNotificationSettingsCard originNames={{ 'plugin:doc-feedback': 'Doc Feedback' }} />
      </SettingsLayout.Route>
    </Route>
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route
      path="/catalog-graph"
      element={<CatalogGraphPage initialState={{ selectedKinds: ['Component', 'API', 'Resource', 'System', 'Domain', 'Group'], maxDepth: 1 }} />}
    />
    <Route path="/announcements" element={<AnnouncementsPage />} />
    <Route path="/self-service" element={<SelfServicePage />} />
    <Route path="/release-notes" element={<ReleaseNotesPage title='Release Notes'/>} />
    <Route path="/feedback" element={<DocFeedbackPage />} />
    <Route
      path="/catalog-unprocessed-entities"
      element={<CatalogUnprocessedEntitiesPage />}
    />;
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
